import { Component, inject } from '@angular/core';

import { LanguageService } from '../services/language.service';

/**
 * Language Switcher Component
 *
 * Provides a simple button to toggle between available languages
 *
 * Usage:
 * ```html
 * <lib-language-switcher />
 * ```
 */
@Component({
  selector: 'lib-language-switcher',
  standalone: true,
  imports: [],
  template: `
    <button
      class="language-switcher"
      (click)="toggleLanguage()"
      [attr.aria-label]="'Switch to ' + nextLanguage()"
      type="button"
    >
      <span class="language-flag">{{ getCurrentFlag() }}</span>
      <span class="language-code">{{ languageService.currentLang() }}</span>
    </button>
  `,
  styles: [`
    .language-switcher {
      display: inline-flex;
      align-items: center;
      gap: 0.5rem;
      padding: 0.5rem 0.75rem;
      background-color: var(--bg-secondary);
      border: 1px solid var(--border-color);
      border-radius: 0.375rem;
      color: var(--text-primary);
      font-size: 0.875rem;
      font-weight: 500;
      cursor: pointer;
      transition: all 0.2s ease;

      &:hover {
        background-color: var(--bg-tertiary);
        border-color: var(--color-primary);
      }

      &:focus {
        outline: 2px solid var(--color-primary);
        outline-offset: 2px;
      }

      &:active {
        transform: scale(0.98);
      }
    }

    .language-flag {
      font-size: 1.25rem;
      line-height: 1;
    }

    .language-code {
      text-transform: uppercase;
      font-family: monospace;
    }
  `]
})
export class LanguageSwitcherComponent {
  protected languageService = inject(LanguageService);

  protected toggleLanguage(): void {
    this.languageService.toggleLanguage();
  }

  protected getCurrentFlag(): string {
    const flags: Record<string, string> = {
      en: '🇬🇧',
      fr: '🇫🇷',
      de: '🇩🇪',
    };
    return flags[this.languageService.currentLang()] || '🌐';
  }

  protected nextLanguage(): string {
    const currentIndex = this.languageService.availableLangs.indexOf(
      this.languageService.currentLang() as any
    );
    const nextIndex = (currentIndex + 1) % this.languageService.availableLangs.length;
    return this.languageService.availableLangs[nextIndex];
  }
}
